import type { HeroView, HomeModel, HomeTab, HomeTabKey, HomeTabModel, HomeZoneModel } from '@hopdrive/feature-portal-home';

import type { PortalProducts } from '@features/products/types';

import type { HomeCounts } from './homeCounts';
import {
  type HomeWorkingRooftop,
  accessRefinement,
  baselineView,
  layoutRefinement,
  offeredRefinement,
  productRefinement,
  resolveIdentity,
  resolveScopeSummary,
} from './homeModelLayers';
import { toDiscoverZone, toHeroZone, toProductCards, toRooftopSummaries, unitLabel } from './mapCountsToHome';
import type { HomeScopeOption } from './utils/homeScopeSelection';

export interface HomeModelInput {
  /** `user.profile.default_role`. */
  role: string | undefined | null;
  /** The composed portal products; undefined before they resolve, which fails closed to the empty shape. */
  products: PortalProducts | undefined;
  heroView: HeroView;
  /** The rooftops the viewer can switch between (the Orders by rooftop cards); more than one means the viewer spans rooftops. */
  rooftopOptions: HomeScopeOption[];
  workingRooftop: HomeWorkingRooftop | undefined;
  counts: HomeCounts;
  activeTab: HomeTabKey;
}

/** Home's one tab. `team` is a reserved key the resolver does not emit. */
const NOW_TAB: HomeTab = { key: 'now', label: 'Now' };

const OPERATIONAL_CATEGORY = 'operational';

const PLACEHOLDERS: HomeZoneModel[] = [
  { key: 'active_orders', placeholder: { title: 'Active orders', description: 'Every order in motion right now, across your products.' } },
  { key: 'up_next', placeholder: { title: 'Up next', description: 'What is scheduled next today.' } },
];

/**
 * Everything Home shows, decided in one place from the viewer's role, the portal products, the
 * scope, the rooftops, and the counts. Layers in order: the baseline view by role, the access
 * refinement, the product refinement, the layout; then the model is assembled: the hero, the
 * product cards for the multi layout, the by-rooftop cards for a manager who spans several rooftops,
 * and the two placeholders. The hero and cards are always the working rooftop's figures. A customer who owns no order type gets the discover hero
 * instead — the products marketed to them and a way to reach a rep — never a bare empty state.
 * Nothing on the page decides for itself, and no setting is read: a module the customer cannot use is
 * absent, one they can use is always present. Team is reserved and not emitted.
 */
export function resolveHomeModel(input: HomeModelInput, callbacks: { onSelectRooftop: (id: number) => void; onContactSales: () => void }): HomeModel {
  const { onSelectRooftop, onContactSales } = callbacks;
  const view = baselineView(input.role);
  const shown = accessRefinement(input.products);
  const { orderTypes } = productRefinement(shown);
  const layout = layoutRefinement(orderTypes);

  const scope = resolveScopeSummary(view, input.rooftopOptions, input.workingRooftop);
  const identity = resolveIdentity(scope, input.workingRooftop);
  const tabs: HomeTab[] = [NOW_TAB];
  const activeTab: HomeTabKey = tabs.some(tab => tab.key === input.activeTab) ? input.activeTab : tabs[0].key;

  // Before the products resolve, nothing is known to be marketed, so the discover hero has no offers.
  if (!input.products) {
    const zones = [toDiscoverZone({ offered: [], entries: {}, onContactSales })];
    return { layout, identity, scope: null, tabs, activeTab, content: [{ key: 'now', zones }] };
  }

  // Appraisals is never an offer.
  if (orderTypes.length === 0) {
    const zones = [toDiscoverZone({ offered: offeredRefinement(shown), entries: shown, onContactSales })];
    return { layout, identity, scope, tabs, activeTab, content: [{ key: 'now', zones }] };
  }

  const operational = input.workingRooftop?.category === OPERATIONAL_CATEGORY;
  const unit = unitLabel(orderTypes, operational);
  // The Orders by rooftop cards show whenever the viewer spans rooftops (scope is non-null exactly then); Home is always scoped to the working rooftop and has no aggregate view.
  const showRooftops = scope !== null;

  const nowZones: HomeZoneModel[] = [
    toHeroZone(input.counts, shown, orderTypes, input.heroView, layout, unit),
    ...(layout === 'multi' ? [toProductCards(input.counts, shown, orderTypes)] : []),
    ...(showRooftops ? [{ key: 'by_rooftop' as const, rooftops: toRooftopSummaries(input.counts, input.rooftopOptions, onSelectRooftop) }] : []),
    ...PLACEHOLDERS,
  ];

  const content: HomeTabModel[] = [{ key: 'now', zones: nowZones }];

  return { layout, identity, scope, tabs, activeTab, content };
}
